import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paginated catalogue: returns { items, total, page, limit, pages, price_range }
export const fetchProducts = async (params = {}) => (await axios.get(`${API}/products`, { params })).data;

export const fetchProductBySlug = async (slug) =>
  (await axios.get(`${API}/products/${encodeURIComponent(slug)}`)).data;

export const fetchProductTypes = async () => (await axios.get(`${API}/products/types`)).data;

export const fetchCategories = async () => (await axios.get(`${API}/categories`)).data;

// ---- Orders (checkout) ----
export const createOrder = async (body) => (await axios.post(`${API}/orders`, body)).data;

export const fetchOrder = async (orderId) =>
  (await axios.get(`${API}/orders/${encodeURIComponent(orderId)}`)).data;

const TOKEN_KEY = "heshaura_token_v1";
const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---- Admin: products ----
export const adminListProducts = async (params = {}) =>
  (await axios.get(`${API}/admin/products`, { headers: authHeaders(), params })).data;

export const adminGetProduct = async (slug) =>
  (await axios.get(`${API}/admin/products/${encodeURIComponent(slug)}`, { headers: authHeaders() })).data;

export const adminUpdateProduct = async (slug, body) =>
  (await axios.patch(`${API}/admin/products/${encodeURIComponent(slug)}`, body, { headers: authHeaders() })).data;

export const adminUploadImage = async (slug, file) => {
  const form = new FormData();
  form.append("file", file);
  return (await axios.post(`${API}/admin/products/${encodeURIComponent(slug)}/images`, form, { headers: authHeaders() })).data;
};

export const adminProductIndex = async () =>
  (await axios.get(`${API}/admin/product-index`, { headers: authHeaders() })).data;

export const adminBulkUploadImages = async (files) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  return (await axios.post(`${API}/admin/products-bulk-images`, form, { headers: authHeaders() })).data;
};

export const adminReplaceImage = async (slug, index, file) => {
  const form = new FormData();
  form.append("file", file);
  return (await axios.post(`${API}/admin/products/${encodeURIComponent(slug)}/images/${index}/replace`, form, { headers: authHeaders() })).data;
};

export const adminDeleteImage = async (slug, index) =>
  (await axios.delete(`${API}/admin/products/${encodeURIComponent(slug)}/images/${index}`, { headers: authHeaders() })).data;

export const adminReorderImages = async (slug, images) =>
  (await axios.post(`${API}/admin/products/${encodeURIComponent(slug)}/images/reorder`, { images }, { headers: authHeaders() })).data;

export const adminDashboard = async () =>
  (await axios.get(`${API}/admin/dashboard`, { headers: authHeaders() })).data;

export const adminCreateProduct = async (body) =>
  (await axios.post(`${API}/admin/products`, body, { headers: authHeaders() })).data;

export const adminDeleteProduct = async (slug) =>
  (await axios.delete(`${API}/admin/products/${encodeURIComponent(slug)}`, { headers: authHeaders() })).data;

export const adminDuplicateProduct = async (slug) =>
  (await axios.post(`${API}/admin/products/${encodeURIComponent(slug)}/duplicate`, {}, { headers: authHeaders() })).data;

export const adminBulkProducts = async (body) =>
  (await axios.post(`${API}/admin/products/bulk`, body, { headers: authHeaders() })).data;

// ---- Admin: types (owner managed) ----
export const adminListTypes = async () =>
  (await axios.get(`${API}/admin/types`, { headers: authHeaders() })).data;

export const adminCreateType = async (name) =>
  (await axios.post(`${API}/admin/types`, { name }, { headers: authHeaders() })).data;

export const adminRenameType = async (slug, name) =>
  (await axios.patch(`${API}/admin/types/${encodeURIComponent(slug)}`, { name }, { headers: authHeaders() })).data;

export const adminDeleteType = async (slug) =>
  (await axios.delete(`${API}/admin/types/${encodeURIComponent(slug)}`, { headers: authHeaders() })).data;

// ---- Admin: categories ----
export const adminListCategories = async () =>
  (await axios.get(`${API}/admin/categories`, { headers: authHeaders() })).data;

export const adminCreateCategory = async (body) =>
  (await axios.post(`${API}/admin/categories`, body, { headers: authHeaders() })).data;

export const adminUpdateCategory = async (slug, body) =>
  (await axios.patch(`${API}/admin/categories/${encodeURIComponent(slug)}`, body, { headers: authHeaders() })).data;

export const adminDeleteCategory = async (slug) =>
  (await axios.delete(`${API}/admin/categories/${encodeURIComponent(slug)}`, { headers: authHeaders() })).data;

export const adminUploadCategoryImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  return (await axios.post(`${API}/admin/categories/image`, form, { headers: authHeaders() })).data;
};

// ---- Customer saved addresses ----
export const listAddresses = async () =>
  (await axios.get(`${API}/addresses`, { headers: authHeaders() })).data;

export const createAddress = async (body) =>
  (await axios.post(`${API}/addresses`, body, { headers: authHeaders() })).data;

export const updateAddress = async (id, body) =>
  (await axios.put(`${API}/addresses/${id}`, body, { headers: authHeaders() })).data;

export const deleteAddress = async (id) =>
  (await axios.delete(`${API}/addresses/${id}`, { headers: authHeaders() })).data;

export const setDefaultAddress = async (id) =>
  (await axios.post(`${API}/addresses/${id}/default`, {}, { headers: authHeaders() })).data;

export const submitWhatsAppLead = async (body) => (await axios.post(`${API}/leads/whatsapp`, body)).data;

export const submitNewsletter = async (email) => (await axios.post(`${API}/leads/newsletter`, { email })).data;

// ---- Offer popup (public + admin) ----
export const fetchOfferPopup = async () => (await axios.get(`${API}/offer-popup`)).data;

// ---- Social links (public + admin) ----
export const fetchSocialLinks = async () => (await axios.get(`${API}/social-links`)).data;

export const adminGetSocialLinks = async () =>
  (await axios.get(`${API}/admin/social-links`, { headers: authHeaders() })).data;

export const adminSaveSocialLinks = async (body) =>
  (await axios.put(`${API}/admin/social-links`, body, { headers: authHeaders() })).data;

// ---- Coupons ----
export const validateCoupon = async (code, subtotal) =>
  (await axios.post(`${API}/coupons/validate`, { code, subtotal })).data;

export const adminListCoupons = async () =>
  (await axios.get(`${API}/admin/coupons`, { headers: authHeaders() })).data;

export const adminCreateCoupon = async (body) =>
  (await axios.post(`${API}/admin/coupons`, body, { headers: authHeaders() })).data;

export const adminUpdateCoupon = async (id, body) =>
  (await axios.put(`${API}/admin/coupons/${id}`, body, { headers: authHeaders() })).data;

export const adminDeleteCoupon = async (id) =>
  (await axios.delete(`${API}/admin/coupons/${id}`, { headers: authHeaders() })).data;

// ---- Admin: order status ----
export const adminUpdateOrderStatus = async (orderId, status) =>
  (await axios.patch(`${API}/admin/orders/${encodeURIComponent(orderId)}/status`, { status }, { headers: authHeaders() })).data;

export const adminGetOfferPopup = async () =>
  (await axios.get(`${API}/admin/offer-popup`, { headers: authHeaders() })).data;

export const adminSaveOfferPopup = async (body) =>
  (await axios.put(`${API}/admin/offer-popup`, body, { headers: authHeaders() })).data;

export const adminUploadOfferImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  return (await axios.post(`${API}/admin/offer-popup/image`, form, { headers: authHeaders() })).data;
};

// ---- Admin: WhatsApp marketing leads ----
export const adminListWhatsAppLeads = async () =>
  (await axios.get(`${API}/admin/whatsapp-leads`, { headers: authHeaders() })).data;

export const sendWhatsAppOtp = async (whatsapp) =>
  (await axios.post(`${API}/auth/send-whatsapp-otp`, { whatsapp })).data;

export const verifyWhatsAppOtp = async (whatsapp, code) =>
  (await axios.post(`${API}/auth/verify-whatsapp-otp`, { whatsapp, code })).data;

export const adminAdjustInventory = async (body) =>
  (await axios.post(`${API}/admin/inventory/adjust`, body, { headers: authHeaders() })).data;

export const adminInventoryLogs = async (slug) =>
  (await axios.get(`${API}/admin/inventory/logs`, { headers: authHeaders(), params: slug ? { slug } : {} })).data;

export const adminLowStock = async () =>
  (await axios.get(`${API}/admin/inventory/low-stock`, { headers: authHeaders() })).data;

export const adminOutOfStock = async () =>
  (await axios.get(`${API}/admin/inventory/out-of-stock`, { headers: authHeaders() })).data;

// ---- Admin: orders ----
export const adminListOrders = async (params = {}) =>
  (await axios.get(`${API}/admin/orders`, { headers: authHeaders(), params })).data;

export const adminGetOrder = async (orderId) =>
  (await axios.get(`${API}/admin/orders/${encodeURIComponent(orderId)}`, { headers: authHeaders() })).data;
